'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  GRAPHICS_PRESETS,
  consumeBootSentinel,
  consumeGraphicsParam,
  detectDefaultLevel,
  invalidateGpuProbeCache,
  isAuto,
  loadStoredLevel,
  storeLevel,
  type BootSentinel,
  type DetectedLevel,
  type GraphicsConfig,
  type GraphicsLevel,
} from './graphics-settings';
import { recordBootPhase } from '@/lib/boot-milestones';

interface GraphicsContextValue {
  level: GraphicsLevel;
  config: GraphicsConfig;
  auto: boolean;
  detected: DetectedLevel;
  setLevel: (level: GraphicsLevel) => void;
  /**
   * Set the in-memory level *without* persisting to `localStorage`.
   *
   * Sole intended caller: `TutorialOverlay`, which forces 'lite' for
   * the duration of the mobile tutorial run. The persistent `setLevel`
   * variant would clobber the user's saved preference with no
   * compensating restore on tutorial exit, leaving them on 2D forever
   * after — which is exactly the regression we're closing.
   */
  setLevelTransient: (level: GraphicsLevel) => void;
  setAuto: () => void;
  /**
   * Persist the active level *only once a 3D canvas has definitely
   * rendered a frame*. `setLevel` / `setAuto` apply a 3D choice in
   * memory but deliberately leave `localStorage` untouched, so a 3D
   * preset that never paints (crash, GPU stall, context loss) is
   * never written — a reload falls back to the previous (2D) choice.
   * `BoardCanvas3D` calls this from its first-frame signal; it's a
   * no-op when there's nothing pending (e.g. a 2D session, which
   * persists eagerly because it can't fail to load). */
  confirmLevelLoaded: () => void;
  /**
   * Set when the runtime watchdog (`webglcontextlost` listener,
   * longtask observer, etc.) detected a GPU stall and forcibly
   * dropped the user to `lite`. Surfaced by `LobbyClient` as a
   * dismissable banner so the user knows why the scene flipped to
   * 2D and how to undo it. `null` when no auto-downgrade is
   * pending dismissal. */
  autoDowngradeReason: string | null;
  /** Wire-up for the runtime watchdog: drop to `lite` *and* publish
   *  a human-readable reason so the lobby can show the banner. Calling
   *  this with the user already on `lite` is a no-op for the level —
   *  but the reason still surfaces, so a context-loss event during a
   *  manually-selected `lite` session still tells the user what
   *  happened. Repeated calls within the same session collapse onto
   *  the most recent reason. */
  reportAutoDowngrade: (reason: string) => void;
  /** Clear the banner. Called from the banner's dismiss button or
   *  whenever the user manually re-picks a non-`lite` level (since
   *  the banner is then meaningless). */
  dismissAutoDowngrade: () => void;
}

const GraphicsContext = createContext<GraphicsContextValue | null>(null);

/** Last completed `blockingDetected()` / idle-deferred probe result. Stays
 *  `null` until the blocking path runs (or defer finishes). NOT the same as
 *  React `detected` state while defer is pending. */
let cachedDetected: DetectedLevel | null = null;

/** Blocking auto-detect: memoises inside `detectDefaultLevel` / `probeGpu`. */
function blockingDetected(): DetectedLevel {
  if (typeof window === 'undefined') {
    return { level: 'high', reason: 'SSR default' };
  }
  cachedDetected ??= detectDefaultLevel();
  return cachedDetected;
}

/** Cached one-shot read of the boot sentinel. A sentinel present on
 *  mount means the *previous* 3D canvas mount wrote it but never
 *  cleared it — i.e. the session never reached first frame. We
 *  consume it (atomic read + delete) at module scope so the three
 *  lazy state initialisers in `GraphicsProvider` all observe the
 *  same result without triple-consuming from storage.
 *
 *  Side effect: when a sentinel is recovered we immediately persist
 *  `('lite', auto=false)` so any subsequent page load — including
 *  ones that race ahead of our React mount — reads the stored
 *  manual `lite` and can't be auto-detected back into the crashing
 *  preset. The user can flip back to auto from the graphics menu
 *  once they're in a working session. See
 *  `docs/investigations/2026-05-03-amd-igpu-strix-halo-freeze.md`
 *  for the loop this closes.
 *
 *  `undefined` until first access; `null` once consumed and no
 *  sentinel was recovered; otherwise the parsed payload. */
let cachedSentinel: BootSentinel | null | undefined = undefined;
function getSentinelOnce(): BootSentinel | null {
  if (cachedSentinel === undefined) {
    cachedSentinel = consumeBootSentinel();
    if (cachedSentinel) {
      try {
        storeLevel('lite', false);
      } catch {
        // best-effort; the in-memory fallback below still holds
      }
    }
  }
  return cachedSentinel;
}

/** Cached one-shot read of the `?graphics=…` URL param. We resolve
 *  it at module scope (first access) so the three lazy `useState`
 *  initialisers in `GraphicsProvider` all observe the same value
 *  without re-running `history.replaceState` three times in a row,
 *  and so subsequent `<GraphicsProvider>` remounts (e.g. across a
 *  `/lobby` → `/create` → `/board/X` round-trip) don't accidentally
 *  re-consume an already-stripped param.
 *
 *  `undefined` until evaluated; `null` once evaluated and there was
 *  no param; otherwise the parsed level / 'auto' marker. */
let cachedParam: GraphicsLevel | 'auto' | null | undefined = undefined;
function getParamOnce(): GraphicsLevel | 'auto' | null {
  if (cachedParam === undefined) {
    cachedParam = consumeGraphicsParam();
    // Side-effect: persist the URL choice immediately so a refresh
    // (or any later mount that doesn't see the param) still picks
    // up the user's intent. `auto` clears the manual override; an
    // explicit level is treated as a manual selection (auto=false).
    if (cachedParam === 'auto') {
      try {
        const det = blockingDetected();
        storeLevel(det.level, true);
      } catch {
        // ignore — best-effort persistence
      }
    } else if (cachedParam) {
      try {
        storeLevel(cachedParam, false);
      } catch {
        // ignore
      }
    }
  }
  return cachedParam;
}

/** Resolve the effective initial preset (level + auto + detected) for
 *  this mount of `<GraphicsProvider>`. The GPU probe is memoised at
 *  module scope; `localStorage` is read fresh every call so a manual
 *  `setLevel('high')` in a previous mount survives a remount. */
function resolveInitialGraphics(): {
  detected: DetectedLevel;
  level: GraphicsLevel;
  auto: boolean;
  /** Set when the previous mount wrote a boot sentinel that never
   *  got cleared — i.e. we're recovering from a crash that killed
   *  the tab before first frame. `GraphicsProvider` surfaces this
   *  through `autoDowngradeReason`. */
  recoveredFromCrash: BootSentinel | null;
  /** Skip synchronous `probeGpu()` on an auto first visit; completed
   *  in an idle callback inside `GraphicsProvider`. */
  deferGpuProbe: boolean;
} {
  if (typeof window === 'undefined') {
    // Everyone starts in 2D — SSR renders lite so there's no 3D flash
    // before the client resolves the (also-lite) default.
    const detected: DetectedLevel = { level: 'lite', reason: 'SSR default (2D)' };
    return {
      detected,
      level: 'lite',
      auto: false,
      recoveredFromCrash: null,
      deferGpuProbe: false,
    };
  }

  // Boot-sentinel recovery runs first. A sentinel present on mount
  // means the previous 3D canvas never painted — overwhelmingly a
  // shader-compile stall on an under-detected iGPU. Force lite this
  // session regardless of what's in localStorage / the URL; the
  // sentinel-consumer already persisted `('lite', auto=false)` so
  // the user doesn't fall back into the crashy preset on next load.
  // The URL param and stored choice are intentionally ignored this
  // mount — the user can undo from the graphics menu once they're
  // actually in the app.
  const sentinel = getSentinelOnce();
  if (sentinel) {
    const det = blockingDetected();
    return {
      detected: det,
      level: 'lite',
      auto: false,
      recoveredFromCrash: sentinel,
      deferGpuProbe: false,
    };
  }

  // URL param takes precedence over localStorage on the first mount
  // it's seen — this is the "DM the user a link" escape hatch
  // documented on `consumeGraphicsParam`. An explicit level overrides
  // any persisted choice; `auto` re-arms the detection pipeline.
  const param = getParamOnce();
  if (param === 'auto') {
    const det = blockingDetected();
    return {
      detected: det,
      level: det.level,
      auto: true,
      recoveredFromCrash: null,
      deferGpuProbe: false,
    };
  }
  if (param) {
    const det = blockingDetected();
    return {
      detected: det,
      level: param,
      auto: false,
      recoveredFromCrash: null,
      deferGpuProbe: false,
    };
  }

  // Everyone starts in 2D. We only honour a stored level when the user
  // explicitly picked it (auto=false) — a fresh visit, or a stored
  // "auto" preference, resolves to lite with no GPU probe. 3D is now a
  // deliberate opt-in from the graphics menu, and a 3D pick only
  // reaches `localStorage` once it has actually rendered a frame
  // (`confirmLevelLoaded`), so an explicit stored 3D level here is one
  // that loaded successfully at least once.
  const stored = loadStoredLevel();
  const useStored = stored !== null && !isAuto();
  if (useStored) {
    return {
      detected: blockingDetected(),
      level: stored,
      auto: false,
      recoveredFromCrash: null,
      deferGpuProbe: false,
    };
  }

  return {
    detected: { level: 'lite', reason: 'Default 2D' },
    level: 'lite',
    auto: false,
    recoveredFromCrash: null,
    deferGpuProbe: false,
  };
}

/** Banner copy for a post-crash recovery. Distinguishes the two
 *  source cases so the message is honest about what happened:
 *  a manual selection specifically didn't load (suggest trying a
 *  lower preset) vs the auto-detected default didn't load (we
 *  picked wrong; user can opt back in if they think we
 *  over-corrected). Short enough for the existing
 *  `reportAutoDowngrade` banner slot. */
function formatRecoveryBanner(sentinel: BootSentinel): string {
  if (sentinel.source === 'manual') {
    return `Switched to 2D — the ${sentinel.level} preset didn't finish loading last time. Try again from graphics settings.`;
  }
  return `Switched to 2D — 3D didn't finish loading last time. Re-enable from graphics settings when ready.`;
}

export function GraphicsProvider({ children }: { children: ReactNode }) {
  const bootRef = useRef<ReturnType<typeof resolveInitialGraphics> | null>(
    null,
  );
  if (bootRef.current === null) {
    bootRef.current = resolveInitialGraphics();
  }
  const boot = bootRef.current;

  const [detected, setDetected] = useState<DetectedLevel>(
    () => boot.detected,
  );
  const [level, setLevelState] = useState<GraphicsLevel>(() => boot.level);
  const [auto, setAutoState] = useState<boolean>(() => boot.auto);
  // Banner copy when the runtime watchdog (`webglcontextlost`,
  // longtask observer) drops the user to `lite`. `null` when no
  // auto-downgrade is currently surfaced.
  //
  // Initial value pulls from the same `resolveInitialGraphics()` read
  // as `level` / `auto` / `detected` above: if the boot sentinel
  // fired (previous mount never painted), we want the banner to show
  // up on the very first render of the new session — not after a
  // useEffect tick, which would let the lobby flash without
  // explanation before the banner appeared.
  const [autoDowngradeReason, setAutoDowngradeReason] = useState<string | null>(
    () =>
      boot.recoveredFromCrash
        ? formatRecoveryBanner(boot.recoveredFromCrash)
        : null,
  );

  useEffect(() => {
    recordBootPhase('graphics.provider_mount', {
      deferGpuProbe: boot.deferGpuProbe,
      level: boot.level,
      auto: boot.auto,
    });
  }, [boot.auto, boot.deferGpuProbe, boot.level]);

  // A level is held here after a 3D pick until the canvas confirms it
  // actually rendered (`confirmLevelLoaded`). `null` means nothing is
  // awaiting confirmation — either we're on 2D (persisted eagerly) or
  // the active 3D level has already been written. Stored as `{ level,
  // auto }` so `confirmLevelLoaded` can persist the exact selection,
  // preserving whether it came from a manual pick or the Auto button.
  const pendingPersistRef = useRef<{ level: GraphicsLevel; auto: boolean } | null>(
    null,
  );

  const setLevel = useCallback((next: GraphicsLevel) => {
    setLevelState(next);
    setAutoState(false);
    // 2D can't fail to load, so persist it immediately. A 3D pick is
    // held in `pendingPersistRef` and only written once the canvas
    // reports a painted frame — see `confirmLevelLoaded`. We do NOT
    // touch `localStorage` for a 3D pick here, so a reload before the
    // first frame falls back to the previous (2D) choice.
    if (GRAPHICS_PRESETS[next].twoD) {
      storeLevel(next, false);
      pendingPersistRef.current = null;
    } else {
      pendingPersistRef.current = { level: next, auto: false };
    }
    // The user actively picked a level — any pending auto-downgrade
    // banner is now stale (the user reasserted control regardless of
    // which level they picked). Clearing here covers both "switch
    // back to 3D" and "stay on lite but acknowledge the choice".
    setAutoDowngradeReason(null);
  }, []);

  // Persist the in-flight 3D selection once a frame has actually
  // rendered. No-op when nothing is pending (2D sessions, or a 3D
  // level already confirmed). Guards on the still-current level so a
  // late first-frame from a canvas the user has since navigated away
  // from can't write a stale preset.
  const confirmLevelLoaded = useCallback(() => {
    const pending = pendingPersistRef.current;
    if (!pending) return;
    storeLevel(pending.level, pending.auto);
    pendingPersistRef.current = null;
  }, []);

  // Runtime-only override that updates React state without persisting
  // to `localStorage`. When the consuming provider unmounts (e.g. the
  // user exits the tutorial route, which mounts its own `LobbyClient`
  // / `GraphicsProvider`), the override is dropped naturally; the
  // surrounding lobby's provider re-reads `localStorage` and observes
  // the user's untouched preference. We also clear `auto` because a
  // forced override is by definition not auto-detected.
  const setLevelTransient = useCallback((next: GraphicsLevel) => {
    setLevelState(next);
    setAutoState(false);
  }, []);

  const setAuto = useCallback(() => {
    invalidateGpuProbeCache();
    cachedDetected = null;
    const det = detectDefaultLevel();
    cachedDetected = det;
    setDetected(det);
    setLevelState(det.level);
    setAutoState(true);
    // Same load-gating as `setLevel`: persist a detected 2D result
    // immediately, but hold a detected 3D result until the canvas
    // confirms a painted frame via `confirmLevelLoaded`.
    if (GRAPHICS_PRESETS[det.level].twoD) {
      storeLevel(det.level, true);
      pendingPersistRef.current = null;
    } else {
      pendingPersistRef.current = { level: det.level, auto: true };
    }
    setAutoDowngradeReason(null);
  }, []);

  // Runtime watchdog hook. Always force the in-memory level to `lite`
  // and surface the reason; the override is intentionally transient
  // (matches `setLevelTransient`) so the user's saved preference is
  // restored on the next mount and a single bad first-paint doesn't
  // pin them to 2D forever. `auto` is cleared so the graphics menu
  // reads as a manual override (the runtime decision *is* effectively
  // a manual override on the user's behalf). Repeated calls within
  // the session collapse onto the latest reason — the banner doesn't
  // stack.
  const reportAutoDowngrade = useCallback((reason: string) => {
    setLevelState('lite');
    setAutoState(false);
    // A downgrade means a 3D level just failed at runtime — drop any
    // pending persistence so the failed preset is never written.
    pendingPersistRef.current = null;
    setAutoDowngradeReason(reason);
  }, []);

  const dismissAutoDowngrade = useCallback(() => {
    setAutoDowngradeReason(null);
  }, []);

  const value = useMemo<GraphicsContextValue>(
    () => ({
      level,
      config: GRAPHICS_PRESETS[level],
      auto,
      detected,
      setLevel,
      setLevelTransient,
      setAuto,
      confirmLevelLoaded,
      autoDowngradeReason,
      reportAutoDowngrade,
      dismissAutoDowngrade,
    }),
    [
      level,
      auto,
      detected,
      setLevel,
      setLevelTransient,
      setAuto,
      confirmLevelLoaded,
      autoDowngradeReason,
      reportAutoDowngrade,
      dismissAutoDowngrade,
    ],
  );

  return <GraphicsContext.Provider value={value}>{children}</GraphicsContext.Provider>;
}

export function useGraphics(): GraphicsContextValue {
  const ctx = useContext(GraphicsContext);
  if (!ctx) {
    throw new Error('useGraphics() must be used inside <GraphicsProvider>');
  }
  return ctx;
}
